// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';

// api
import {
  useGetAuthorizationsByCvrQuery,
  useGetDatahubAccountByCVRQuery,
  useGetMeteringPointsByCvrQuery,
  usePatchDatahubIntegrationAccountMutation,
} from '@/api/datahub-integration/datahubIntegrationApiSlice';

// shared components
import { AdministratorOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// components
import MeteringPointsDatagrid from '@/features/datahub-integration/MeteringPointsDatagrid';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export const Route = createFileRoute(
  '/$accountId/_user/datahub-integration/$cvrNumber',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
});

function RouteComponent() {
  const { t } = useTranslation('datahub-integration');
  const { accountId, cvrNumber } = useParams({ strict: false });
  const dispatch = useDispatch();

  // Fetch account details
  const {
    data: account,
    isLoading: isLoadingAccount,
    isError: isErrorAccount,
  } = useGetDatahubAccountByCVRQuery(
    { cvr_number: cvrNumber, recalc_account_id: accountId },
    {
      skip: !accountId && !cvrNumber,
    },
  );

  const [limitedMeters, setLimitedMeters] = useState<string[]>([]);
  const [excludedMeters, setExcludedMeters] = useState<string[]>([]);
  const [includeChildMeteringPoints, setIncludeChildMeteringPoints] =
    useState(false);
  const [prevAccountKey, setPrevAccountKey] = useState<string | null>(null);

  // Reset form state when navigating to a different account (adjust state during render)
  const currentAccountKey = `${accountId}-${cvrNumber}`;
  if (account && currentAccountKey !== prevAccountKey) {
    setPrevAccountKey(currentAccountKey);
    setLimitedMeters(
      account.LimitedMetersList
        ? account.LimitedMetersList.map((id: unknown) => String(id))
        : [],
    );
    setExcludedMeters(
      account.ExcludeMetersList
        ? account.ExcludeMetersList.map((id: unknown) => String(id))
        : [],
    );
    setIncludeChildMeteringPoints(account.IncludeChildMeteringPoints ?? false);
  }

  // Fetch metering points from authorizations
  const {
    data: meters,
    isLoading: isLoadingMeters,
    isError: isErrorMeters,
    error: errorMeters,
  } = useGetMeteringPointsByCvrQuery(cvrNumber, {
    skip: !cvrNumber,
  });

  // Fetch authorizations
  const { data: authorizations, isLoading: isLoadingAuthorizations } =
    useGetAuthorizationsByCvrQuery(cvrNumber, {
      skip: !cvrNumber,
    });

  const [patchAccount, { isLoading: isUpdating }] =
    usePatchDatahubIntegrationAccountMutation();

  const handleSaveChanges = async () => {
    if (!account) return;

    const result = await patchAccount({
      recalc_account_id: account.ReCalcAccountId,
      cvr_number: account.CustomerCVR,
      guard_flag: false,
      limited_meters_list: limitedMeters.length > 0 ? limitedMeters : undefined,
      exclude_meters_list:
        excludedMeters.length > 0 ? excludedMeters : undefined,
      include_child_metering_points: includeChildMeteringPoints,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('detail.save_success'),
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleToggleLimited = (meterId: string) => {
    setLimitedMeters((prev) => {
      if (prev.includes(meterId)) {
        return prev.filter((id: string) => id !== meterId);
      } else {
        // Clear exclude list when adding to include list
        setExcludedMeters([]);
        return [...prev, meterId];
      }
    });
  };

  const handleToggleExcluded = (meterId: string) => {
    setExcludedMeters((prev) => {
      if (prev.includes(meterId)) {
        return prev.filter((id: string) => id !== meterId);
      } else {
        // Clear include list when adding to exclude list
        setLimitedMeters([]);
        return [...prev, meterId];
      }
    });
  };

  const handleClearLimited = () => {
    setLimitedMeters([]);
  };

  const handleClearExcluded = () => {
    setExcludedMeters([]);
  };

  const handleExternalGrantLink = () => {
    const fromDate = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
    const toDate = dayjs().add(3, 'year').format('YYYY-MM-DD');
    const path = `https://eloverblik.dk/power-of-attorney?thirdPartyId=23C949D6-394A-4001-94EC-B4F55E9CF850&fromDate=${fromDate}&toDate=${toDate}&customerKey=${account?.ReCalcAccountId}`;
    window.open(path, '_blank');
  };

  if (isLoadingAccount || isLoadingMeters) {
    return (
      <Box height={'60vh'}>
        <GatewayLoadingIndicator
          isLoading={isLoadingAccount || isLoadingMeters}
          loadingText={t('loading_page_text')}
          disableBackdrop={true}
        />
      </Box>
    );
  }

  if (
    isErrorMeters &&
    isFetchBaseQueryError(errorMeters) &&
    errorMeters.status === 504
  ) {
    return (
      <PageContainer maxWidth="xl" sx={{ py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '80vh',
          }}
        >
          <div>
            <img
              className="error-image"
              src="https://cdn-icons-png.flaticon.com/512/4539/4539472.png"
              alt=""
              style={{ height: '150px' }}
            ></img>
          </div>
          <Stack spacing={2}>
            <Typography variant="h6" align="center">
              {t('timeout.error_timeout_title')}
            </Typography>
            <Typography variant="body1" align="center">
              {t('timeout.error_timeout_explanation')}
            </Typography>
            <Typography variant="body1" align="center">
              {t('timeout.error_timeout_workaround')}
            </Typography>
          </Stack>
        </Box>
      </PageContainer>
    );
  }

  if (isErrorAccount || !account) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">{t('detail.error_loading')}</Alert>
      </Container>
    );
  }

  const hasChanges =
    JSON.stringify([...limitedMeters].sort()) !==
      JSON.stringify([...(account.LimitedMetersList || [])].sort()) ||
    JSON.stringify([...excludedMeters].sort()) !==
      JSON.stringify([...(account.ExcludeMetersList || [])].sort()) ||
    includeChildMeteringPoints !==
      (account.IncludeChildMeteringPoints ?? false);

  return (
    <PageContainer maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        {/* Header Card */}
        <Card>
          <CardHeader
            title={t('detail.title')}
            subheader={t('detail.cvr_label', {
              cvr: account.CustomerCVR || 'N/A',
            })}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('detail.account_id')}
                </Typography>
                <Typography variant="body1">
                  {account.ReCalcAccountId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('detail.company_name')}
                </Typography>
                <Typography variant="body1">
                  {account.CustomerName || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('detail.authorization_period')}
                </Typography>
                <Typography variant="body1">
                  {account.ValidFrom && account.ValidTo
                    ? `${new Date(account.ValidFrom).toLocaleDateString()} - ${new Date(account.ValidTo).toLocaleDateString()}`
                    : 'N/A'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.limited_meters')}
                  </Typography>
                  <Chip
                    label={limitedMeters.length}
                    color={limitedMeters.length > 0 ? 'primary' : 'default'}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.excluded_meters')}
                  </Typography>
                  <Chip
                    label={excludedMeters.length}
                    color={excludedMeters.length > 0 ? 'error' : 'default'}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.include_children')}
                  </Typography>
                  <Chip
                    label={account.IncludeChildMeteringPoints ? 'Yes' : 'No'}
                    color={
                      account.IncludeChildMeteringPoints ? 'success' : 'default'
                    }
                  />
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Actions */}
        <AdministratorOnly>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleClearLimited}
              disabled={limitedMeters.length === 0}
            >
              {t('detail.clear_limited')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleClearExcluded}
              disabled={excludedMeters.length === 0}
            >
              {t('detail.clear_excluded')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isUpdating}
            >
              {isUpdating ? t('detail.saving') : t('detail.save')}
            </Button>
          </Stack>
        </AdministratorOnly>

        {/* Meters Grid */}
        <MeteringPointsDatagrid
          meters={meters}
          isLoadingMeters={isLoadingMeters}
          isErrorMeters={isErrorMeters}
          limitedMeters={limitedMeters}
          excludedMeters={excludedMeters}
          onToggleLimited={handleToggleLimited}
          onToggleExcluded={handleToggleExcluded}
        />

        {/* Authorizations Card */}
        <Card>
          <CardHeader
            title={t('detail.authorizations')}
            action={
              <AdministratorOnly>
                <Button
                  variant="contained"
                  onClick={handleExternalGrantLink}
                  disabled={!account}
                >
                  {t('give-access.button')}
                </Button>
              </AdministratorOnly>
            }
          />
          <CardContent>
            {isLoadingAuthorizations ? (
              <CircularProgress />
            ) : authorizations && authorizations.length > 0 ? (
              <Stack spacing={2}>
                <Typography variant="h6">
                  {t('detail.auth_count')} {authorizations.length}
                </Typography>
                {authorizations.map((auth, index) => (
                  <Card key={index}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('detail.third_party')}
                          </Typography>
                          <Typography variant="body1">
                            {auth.thirdPartyName}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('detail.customer')}
                          </Typography>
                          <Typography variant="body1">
                            {t('detail.customer_cvr', {
                              name: auth.customerName,
                              cvr: auth.customerCVR,
                            })}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('detail.valid_period')}
                          </Typography>
                          <Typography variant="body1">
                            {new Date(auth.validFrom).toLocaleDateString()} -{' '}
                            {new Date(auth.validTo).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={
                              auth.includeFutureMeteringPoints
                                ? t('detail.includes_future')
                                : t('detail.no_future')
                            }
                            size="small"
                            color={
                              auth.includeFutureMeteringPoints
                                ? 'primary'
                                : 'default'
                            }
                          />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">
                {t('detail.no_auth')}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>
    </PageContainer>
  );
}
